import './WinnerSelectionTableKO.scss';
import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { ICustomComponentProps } from '@molecules/formbuilder/controls/CustomComponent/ICustomComponentProps';
import { IOffer, IOfferAction } from '@models/winnerSelection/ITableDemand';
import { ITableKOItem } from '@models/winnerSelection/ITableKO';
import OfferCustomForm from '../OfferCustomForm';
import OfferAttachmentsArchive from '../OfferAttachmentsArchive';
import OfferHeaderMenuDialog from '../OfferHeaderMenuDialog';
import Hint from '@srm/ui/hint';
import AuroraLoader from '@srm/ui/aurora-loader';

import { useTableKOData } from '../hooks/useTableKOData';
import { useWinnerTableScroll } from '../hooks/useWinnerTableScroll';
import { useTableViewMode } from '../hooks/useTableViewMode';
import { useFontScale } from '../hooks/useFontScale';
import { OfferHeaderKO } from '../parts/OfferHeaderKO';
import { OfferCellKO } from '../parts/OfferCellKO';
import { HorizontalPager } from '../parts/HorizontalPager';
import { ViewModeToggle } from '../parts/ViewModeToggle';
import { buildOfferMenuActionsKO } from '../parts/buildOfferMenuActionsKO';

export type IWinnerSelectionTableKOProps<TFieldValues extends object = object> = ICustomComponentProps<TFieldValues>;

const WinnerSelectionTableKO = <TFieldValues extends object = object>({
    component,
    docId,
}: IWinnerSelectionTableKOProps<TFieldValues>) => {
    const tableKey = component.params?.tableKey;

    const { data: tableData, isLoading, errorText, reload } = useTableKOData(docId, tableKey);
    const { data: items, offers, settings } = tableData ?? {};

    /* ─── Ориентация таблицы (персист localStorage, общий ключ с Demand) ─── */
    const [view, setView] = useTableViewMode();

    /* ─── Масштаб шрифта: читаем значение (управляется из навбара) и выставляем
     *  --ws-font-scale на корень таблицы. KO всегда standalone. ─── */
    const fontScale = useFontScale();

    /* Локальный modal-state — KO-таблица имеет всего одну модалку (CustomForm),
       поэтому полный useOfferModals overkill. */
    const [customFormOffer, setCustomFormOffer] = useState<IOffer>();
    const [customFormAction, setCustomFormAction] = useState<IOfferAction>();

    const openCustomForm = useCallback((offer: IOffer, action: IOfferAction) => {
        setCustomFormOffer(offer);
        setCustomFormAction(action);
    }, []);
    const closeCustomForm = useCallback(() => {
        setCustomFormOffer(undefined);
        setCustomFormAction(undefined);
    }, []);

    const [archive, setArchive] = useState<{ offerKey: string; action: IOfferAction }>();
    const openArchive = useCallback((offer: IOffer, action: IOfferAction) => {
        setArchive({ offerKey: offer.key, action });
    }, []);
    const closeArchive = useCallback(() => setArchive(undefined), []);

    /* Горизонтальный скролл */
    const { scrollRef, canScrollLeft, canScrollRight, columnWidth, firstColWidth, scrollByPage } = useWinnerTableScroll(
        view,
        tableData,
    );

    /* ─── Render: loading / empty / main ─── */
    if (isLoading && !tableData) {
        return (
            <div className="ws-ko-wrapper">
                {errorText ? (
                    <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
                ) : null}
                <div className="ws-ko__loading">
                    <AuroraLoader label="Загрузка" />
                </div>
            </div>
        );
    }

    if (items === undefined || offers === undefined) {
        return (
            <div className="ws-ko-wrapper">
                {errorText ? (
                    <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
                ) : null}
            </div>
        );
    }

    if (offers.length === 0) {
        return (
            <div className="ws-ko-wrapper">
                <div className="ws-ko__empty">
                    <h1>{settings?.emptyTextHeader}</h1>
                    <p>{settings?.emptyText}</p>
                </div>
            </div>
        );
    }

    /* ─── Переиспользуемые ячейки (одинаковы в обоих видах) ─── */
    const renderOfferHeaderKO = (offer: IOffer) => (
        <OfferHeaderMenuDialog list={buildOfferMenuActionsKO(offer, tableKey!, openCustomForm, openArchive)}>
            <OfferHeaderKO offer={offer} settings={settings ?? {}} />
        </OfferHeaderMenuDialog>
    );

    const renderOfferCellKO = (crit: ITableKOItem, offer: IOffer) => (
        <OfferCellKO
            crit={crit}
            offer={offer}
            settings={settings}
            demandId={docId}
            tableKey={tableKey!}
            onReload={reload}
        />
    );

    /* Угловая ячейка: подпись первого столбца + переключатель ориентации. */
    const cornerCell = (
        <th className="ws-th ws-th-crit">
            <div className="ws-corner">
                <span className="ws-corner__label">{view === 'byCriteria' ? 'Критерий' : 'Предложение'}</span>
                <ViewModeToggle value={view} onChange={setView} />
            </div>
        </th>
    );

    const columnsCount = view === 'byCriteria' ? offers.length : items.length;

    return (
        <div className="ws-ko-wrapper" style={{ '--ws-font-scale': fontScale.scale } as React.CSSProperties}>
            {errorText ? (
                <Hint role="alert" title={`Ошибка загрузки: ${errorText}`} tone="danger" maxWidth="100%" />
            ) : null}

            <div className="ws-scroll" ref={scrollRef}>
                <table className={clsx('ws-table', { 'ws-table--by-offer': view === 'byOffer' })} role="grid">
                    {view === 'byCriteria' ? (
                        <>
                            <colgroup>
                                <col className="ws-col-crit" />
                                {offers.map((o) => (
                                    <col key={o.key} className="ws-col-offer" />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    {cornerCell}
                                    {offers.map((offer) => (
                                        <th key={offer.key} className="ws-th">
                                            {renderOfferHeaderKO(offer)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((crit) => (
                                    <tr key={crit.critId}>
                                        <td className="ws-td ws-td-crit">{crit.critName}</td>
                                        {offers.map((offer) => (
                                            <td key={offer.key} className="ws-td">
                                                {renderOfferCellKO(crit, offer)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </>
                    ) : (
                        <>
                            <colgroup>
                                <col className="ws-col-offerhead" />
                                {items.map((crit) => (
                                    <col key={crit.critId} className="ws-col-critcol" />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    {cornerCell}
                                    {items.map((crit) => (
                                        <th key={crit.critId} className="ws-th ws-th-critcol">
                                            <span className="ws-ko-critcol__title">{crit.critName}</span>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {offers.map((offer) => (
                                    <tr key={offer.key}>
                                        <td className="ws-td ws-td-offerhead">{renderOfferHeaderKO(offer)}</td>
                                        {items.map((crit) => (
                                            <td key={crit.critId} className="ws-td">
                                                {renderOfferCellKO(crit, offer)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </>
                    )}
                </table>
            </div>

            <HorizontalPager
                columnsCount={columnsCount}
                columnWidth={columnWidth}
                firstColWidth={firstColWidth}
                scrollRef={scrollRef}
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight}
                onScroll={scrollByPage}
            />

            {customFormOffer && customFormAction ? (
                <OfferCustomForm
                    tableKey={tableKey!}
                    value={customFormOffer}
                    action={customFormAction}
                    onClose={closeCustomForm}
                />
            ) : null}
            {archive ? (
                <OfferAttachmentsArchive
                    tableKey={tableKey!}
                    offerKey={archive.offerKey}
                    action={archive.action}
                    onClose={closeArchive}
                />
            ) : null}
        </div>
    );
};

export default WinnerSelectionTableKO;
